import { FC } from "react";
import { Link } from "react-router-dom";
import withPage from "../../components/Page";
import Section from "../../components/Section";
import Hero from "../../components/Hero";
import CtaSection from "../../components/CtaSection";
import { globalContent } from "../../config/content";
import styles from "./DigitalOM.module.scss";

const DigitalOM: FC = () => {
  return (
    <div className={styles.digitalOmPage}>
      {/* 1. Hero */}
      <Hero
        eyebrow="Digital O&M"
        heading="Turn the O&M manual into something people actually use."
        copy="O&M information shouldn't disappear into a collection of PDFs and folders once a project is handed over. Pulse turns existing O&M information into a structured, searchable digital resource that building teams can access when they need it."
        backgroundImage={require("../../assets/images/digital-om/digital-om-hero.webp")}
        button={{
          text: "Explore the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />

      {/* 2. The Problem */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>The Challenge</span>
            <h2 className={styles.sectionHeading}>
              The O&M manual is full of valuable information. Finding it
              shouldn't be difficult.
            </h2>
            <p className={styles.sectionCopy}>
              A completed building can contain thousands of pages of technical
              information including equipment manuals, specifications, drawings,
              certificates, asset data and maintenance procedures.
            </p>
            <p className={styles.sectionCopy}>
              Once handed over, information spread across folders and PDFs
              requires manual searching or relying on individuals who know where
              things are stored.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/digital-om/digital-om-fragmented.webp")}
              alt="Fragmented O&M files"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 3. Move Beyond the PDF */}
      <Section bgColor="white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/digital-om/digital-om-vs-pdf.webp")}
              alt="Traditional O&M vs Pulse digital O&M platform"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Beyond PDFs</span>
            <h2 className={styles.sectionHeading}>Move beyond the PDF.</h2>
            <p className={styles.sectionCopy}>
              Traditional O&M manuals remain valuable, but the way people access
              them can be improved. Pulse provides a structured digital
              environment around the information, making it easier to navigate,
              search and connect related records.
            </p>
            <div className={styles.actionLink}>
              <Link
                to="/digital-building-information"
                className={styles.textLink}
              >
                Explore Digital Building Information →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 4. What Pulse Does With O&M Information */}
      <Section bgColor="off-white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Structured Records</span>
          <h2 className={styles.sectionHeading}>
            Bring the information together in one place.
          </h2>
        </div>
        <div className={styles.cardsGrid}>
          <div className={styles.cardItem}>
            <h3>Documents</h3>
            <p>Technical manuals, specifications and records.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Drawings</h3>
            <p>As-built and base-build drawing information.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Assets</h3>
            <p>Equipment records linked to technical data.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Certifications</h3>
            <p>Compliance records and certificates.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Maintenance</h3>
            <p>Service and maintenance procedures.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Manufacturer Info</h3>
            <p>Datasheets and supplier details.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Procedures</h3>
            <p>Operating instructions and guides.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Schedules</h3>
            <p>Building schedules and structured data.</p>
          </div>
        </div>
      </Section>

      {/* 5. Structure Makes O&M More Useful */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Information Architecture</span>
            <h2 className={styles.sectionHeading}>
              The value is in how people access it.
            </h2>
            <p className={styles.sectionCopy}>
              Pulse organises O&M information around systems, assets and
              technical records so users don't have to treat every document as
              an isolated file.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/digital-om/digital-om-lifecycle.webp")}
              alt="O&M structure and lifecycle"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 6. Find Information Faster */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/search/search-problem.webp")}
              alt="Pulse search for O&M records"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Fast Search</span>
            <h2 className={styles.sectionHeading}>
              Stop searching through the manual for one answer.
            </h2>
            <p className={styles.sectionCopy}>
              Pulse search spans the entire O&M and building record, helping
              users find relevant manuals, drawings and assets instantly.
            </p>
            <div className={styles.actionLink}>
              <Link to="/search" className={styles.textLink}>
                Explore Search →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 7. Access Information On Site */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>On-Site Access</span>
            <h2 className={styles.sectionHeading}>
              Information should be available where the work happens.
            </h2>
            <p className={styles.sectionCopy}>
              Engineers and facilities teams can access O&M records on site
              using phones or tablets while standing next to the equipment they
              are servicing.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/mobile/engineer-using-pulse.webp")}
              alt="Engineer using Pulse on mobile"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 8. QR Codes and Asset Access */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/assets/asset-qr-code.webp")}
              alt="QR code asset scanning"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>QR Codes</span>
            <h2 className={styles.sectionHeading}>
              Go from the equipment to its information.
            </h2>
            <p className={styles.sectionCopy}>
              Scan a QR code on physical equipment to jump straight to its Pulse
              record and access connected O&M manuals, drawings and maintenance
              guides.
            </p>
            <div className={styles.actionLink}>
              <Link to="/asset-information" className={styles.textLink}>
                Explore Asset Information →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 9. AI and O&M Information */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Pulse AI</span>
            <h2 className={styles.sectionHeading}>
              Ask questions about the building's information.
            </h2>
            <p className={styles.sectionCopy}>
              Ask natural-language questions about O&M records—such as filter
              replacement intervals or equipment warranties—and receive answers
              grounded in your building's actual source documents.
            </p>
            <div className={styles.actionLink}>
              <Link to="/ai" className={styles.textLink}>
                Explore AI →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-information.webp")}
              alt="Pulse AI O&M queries"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 11. Pulse Doesn't Create O&M Information */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/digital-om/digital-om-hero.webp")}
              alt="O&M specialists and Pulse platform"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Platform Model</span>
            <h2 className={styles.sectionHeading}>
              You create the information. Pulse makes it useful.
            </h2>
            <p className={styles.sectionCopy}>
              O&M specialists create, validate and compile technical records.
              Pulse provides the digital platform that makes that information
              easy to organise, access, search and use throughout the building's
              operational life.
            </p>
          </div>
        </div>
      </Section>

      {/* 12. Why Digital O&M Matters */}
      <Section bgColor="white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Benefits</span>
          <h2 className={styles.sectionHeading}>
            Make valuable building information easier to use.
          </h2>
        </div>
        <div className={styles.cardsGrid}>
          <div className={styles.cardItem}>
            <h3>Find information faster</h3>
            <p>
              Reduce time spent searching through disconnected folders and PDFs.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>Improve accessibility</h3>
            <p>Give building teams access from the office or on site.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>Preserve knowledge</h3>
            <p>
              Create a consistent digital record independent of individuals.
            </p>
          </div>
          <div className={styles.cardItem}>
            <h3>Make handover useful</h3>
            <p>Keep O&M data accessible long after project handover.</p>
          </div>
        </div>
      </Section>

      {/* 14. Final CTA */}
      <CtaSection
        eyebrow="Get Started"
        heading="Make your building information easier to use."
        copy="Whether modernising O&M, improving handover access or giving teams a better way to work with technical documentation, Pulse provides a structured digital platform."
        button={{
          text: "Explore the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />
    </div>
  );
};

export default withPage(DigitalOM)({
  meta: globalContent.seo.digitalOm,
});
